import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Gift, Cake, PartyPopper, Sun, GraduationCap, Baby, ArrowRight, MessageCircle } from "lucide-react";

const giftUses = [
  { icon: Cake, label: "Birthday Gifts", color: "bg-rose-100 text-rose-500" },
  { icon: PartyPopper, label: "Return Gifts", color: "bg-amber-100 text-amber-600" },
  { icon: Sun, label: "Diwali / Rakhi / Christmas", color: "bg-orange-100 text-orange-500" },
  { icon: GraduationCap, label: "Pre-school Graduation", color: "bg-indigo-100 text-indigo-500" },
  { icon: Baby, label: "New Baby Welcome", color: "bg-green-100 text-green-600" },
];

export default function GiftingSection() {
  return (
    <section id="gifting" className="py-20 sm:py-28 bg-amber-50 relative overflow-hidden" data-testid="gifting-section">
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-rose-100/30 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-rose-900/10 border border-white/50">
              <img
                src="https://images.pexels.com/photos/269887/pexels-photo-269887.jpeg"
                alt="Beautifully wrapped gift boxes"
                className="w-full h-[350px] sm:h-[420px] object-cover"
                data-testid="gifting-image"
              />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-4 -right-4 sm:bottom-6 sm:right-6 bg-white rounded-2xl p-4 shadow-xl border border-indigo-950/5">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-rose-500" />
                <span className="text-sm font-bold text-indigo-950">Bulk orders available!</span>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="text-sm font-bold text-rose-500 uppercase tracking-widest mb-3 block">Perfect Gift</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-indigo-950 tracking-tight mb-4" data-testid="gifting-heading">
              The Birthday Gift That Makes You the Favourite Uncle/Aunty
            </h2>
            <p className="text-base sm:text-lg text-indigo-950/50 leading-relaxed mb-8">
              Tired of giving the same old toys and clothes? Give a gift so unique, parents will WhatsApp you saying "Where did you find this?!"
            </p>

            {/* Use cases grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
              {giftUses.map((g, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2.5 bg-white rounded-2xl px-4 py-3 border border-indigo-950/5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  data-testid={`gift-use-${i + 1}`}
                >
                  <div className={`w-8 h-8 rounded-lg ${g.color} flex items-center justify-center flex-shrink-0`}>
                    <g.icon className="w-4 h-4" />
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-indigo-950/70">{g.label}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="order-gift-button"
                className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-bold px-8 py-6 shadow-xl shadow-rose-500/25 hover:-translate-y-1 hover:shadow-2xl transition-all group"
              >
                Order a Gift Book
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                data-testid="bulk-order-whatsapp-button"
                variant="outline"
                className="rounded-full border-2 border-green-500/30 text-green-600 font-semibold px-8 py-6 hover:bg-green-50 hover:border-green-500/50 transition-all"
                onClick={() => window.open("https://wa.me/919999999999?text=Hi!%20I%27m%20interested%20in%20bulk%20orders%20for%20Balakatha%20books", "_blank")}
              >
                <MessageCircle className="w-4 h-4 mr-1" />
                Bulk Orders? WhatsApp Us
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
